import { useCallback, useRef } from "react";

// Must match .timeline-track-label width (3rem = 48px at default font size)
const TRACK_LABEL_WIDTH = 48;

interface PlayheadProps {
  currentTime: number;
  totalDuration: number;
  pixelsPerSecond: number;
  onSeek: (time: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

export default function Playhead({
  currentTime,
  totalDuration,
  pixelsPerSecond,
  onSeek,
  onScrubStart,
  onScrubEnd,
}: PlayheadProps) {
  const left = currentTime * pixelsPerSecond;
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      onScrubStart?.();

      const container = containerRef.current?.parentElement;
      if (!container) return;

      const seek = (clientX: number) => {
        const rect = container.getBoundingClientRect();
        // Account for the 3rem left margin
        const trackLeft = rect.left + TRACK_LABEL_WIDTH;
        const x = clientX - trackLeft + container.scrollLeft;
        const time = Math.max(0, Math.min(x / pixelsPerSecond, totalDuration));
        onSeek(time);
      };

      seek(e.clientX);

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDraggingRef.current) return;
        seek(e.clientX);
      };

      const handleMouseUp = () => {
        isDraggingRef.current = false;
        onScrubEnd?.();
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [pixelsPerSecond, totalDuration, onSeek, onScrubStart, onScrubEnd],
  );

  return (
    <div ref={containerRef}>
      <div
        className="timeline-playhead-drag"
        style={{ left: `${left + TRACK_LABEL_WIDTH}px` }}
        onMouseDown={handleMouseDown}
      />
    </div>
  );
}
